import React from 'react'
import { useTranslation } from 'react-i18next'
import { Lock } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { formatDateTimeLocal } from '@/utils/timezone'
import type { Photo } from '../types'
import PhotoTileSkeleton from './PhotoTileSkeleton'

export const PHOTOS_DRAG_TYPE = 'application/x-urkla-photos'

interface Props {
  photo: Photo
  size: 'S' | 'M' | 'L'
  selected: boolean
  selectionActive: boolean
  /** Ids a drag of this tile carries: the whole selection when this tile is selected, else just the tile. */
  dragIds: string[]
  onToggle: (e: React.MouseEvent) => void
  onOpen: () => void
  /** Album view only: a photos drag dropped on this tile (before = left half). */
  onReorderDrop?: (draggedIds: string[], targetId: string, before: boolean) => void
  onContextMenu?: (e: React.MouseEvent) => void
}

const PhotoTile: React.FC<Props> = ({ photo, size, selected, selectionActive, dragIds, onToggle, onOpen, onReorderDrop, onContextMenu }) => {
  const { t, i18n } = useTranslation()
  const time = formatDateTimeLocal(photo.capturedAt, i18n.language, 'p')

  const handleClick = (e: React.MouseEvent) => {
    if (e.shiftKey || selectionActive) onToggle(e)
    else onOpen()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    try {
      const ids = JSON.parse(e.dataTransfer.getData(PHOTOS_DRAG_TYPE)) as string[]
      const rect = e.currentTarget.getBoundingClientRect()
      onReorderDrop?.(ids, photo.id, e.clientX < rect.left + rect.width / 2)
    } catch (err) {
      // not a photos drag
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      draggable
      onDragStart={(e) => e.dataTransfer.setData(PHOTOS_DRAG_TYPE, JSON.stringify(dragIds))}
      onDragOver={onReorderDrop ? (e) => e.dataTransfer.types.indexOf(PHOTOS_DRAG_TYPE) >= 0 && e.preventDefault() : undefined}
      onDrop={onReorderDrop ? handleDrop : undefined}
      onClick={handleClick}
      onContextMenu={onContextMenu}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen()
        if (e.key === ' ') {
          e.preventDefault()
          onToggle(e as unknown as React.MouseEvent)
        }
      }}
      className={cn(
        'group relative aspect-square cursor-pointer overflow-hidden rounded-md bg-muted outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected && 'ring-2 ring-primary',
      )}
    >
      {photo.status === 'PROCESSING' && <PhotoTileSkeleton />}
      {photo.status === 'READY' && photo.thumbUrl && (
        <img src={photo.thumbUrl} alt={photo.title || photo.filename || ''} loading="lazy" draggable={false} className="h-full w-full object-cover" />
      )}
      {photo.status === 'FAILED' && (
        <div className="flex h-full w-full items-center justify-center">
          <Badge variant="danger" className="whitespace-nowrap">{t('Photos_status_failed')}</Badge>
        </div>
      )}

      <div
        className={cn('absolute left-1.5 top-1.5', selectionActive || selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100')}
        onClick={(e) => e.stopPropagation()}
      >
        <Checkbox
          checked={selected}
          aria-label={t('Photos_select_photo')}
          className="bg-background/90"
          onClick={(e) => {
            e.stopPropagation()
            onToggle(e)
          }}
        />
      </div>

      <div className="absolute right-1.5 top-1.5 flex items-center gap-1">
        {photo.timeSource === 'UPLOAD' && (
          <Badge variant="warning" className="whitespace-nowrap px-1.5 py-0 text-[10px]">{t('Photos_no_exif_time')}</Badge>
        )}
        {photo.visibility === 'INTERNAL' && (
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-background/90 text-muted-foreground">
            <Lock className="h-3 w-3" />
          </span>
        )}
      </div>

      {size !== 'S' && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 pt-6 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
          {photo.title && <div className="truncate font-medium">{photo.title}</div>}
          <div className="truncate">{time}</div>
          {photo.tags.length > 0 && <div className="truncate opacity-80">{photo.tags.map((tag) => tag.name).join(', ')}</div>}
        </div>
      )}
    </div>
  )
}

export default React.memo(PhotoTile)
